<template>
  <section
    v-if="currentPackage"
    class="package-contingent my-4"
    :class="baseTypeThemeClass"
    :style="baseTypeThemeStyle"
  >
    <div class="package-contingent__header">
      <div>
        <div class="package-contingent__eyebrow">Aktives Paket: {{ packageTypeLabel }}</div>
        <h3 class="package-contingent__title mb-2">{{ titleLabel }}</h3>
        <p class="package-contingent__subtitle mb-0">{{ capacityLabel }}</p>
      </div>
      <div class="package-contingent__header-right">
        <router-link :to="{name: createRouteName}" class="btn package-contingent__action-btn">
          {{ createLabel }}
        </router-link>
      </div>
    </div>

    <ProgressBar
      :class="{warning: calcCurrentProgress > 0.8 && calcCurrentProgress < 1, danger: calcCurrentProgress >= 1}"
      :value="calcCurrentProgress * 100"
    >
      {{ progressLabel }}
    </ProgressBar>

    <div class="package-contingent__meta">
      <span class="package-contingent__meta-item package-contingent__meta-item--mvp">
        <strong>{{ pluralLabel }} auf mvp.de:</strong> {{ freeQuantity ? `noch ${freeQuantity} frei` : 'keine frei' }}
      </span>
      <span v-for="host in websiteHosts" :key="host" class="package-contingent__meta-item package-contingent__meta-item--unlimited">
        <strong>{{ pluralLabel }} auf {{ host }}:</strong> unbegrenzt
      </span>
      <span class="package-contingent__meta-item package-contingent__meta-item--unlimited">
        <strong>Entwürfe:</strong> unbegrenzt
      </span>
    </div>

    <FreePackageHint v-if="isFreePackage" :route-query="$route.query" />

    <div v-if="isExhausted" class="package-contingent__limit-note">
      Alle Plätze auf mvp.de sind belegt. Um einen weiteren Eintrag auf mvp.de zu zeigen, nehmen Sie einen
      anderen Eintrag von mvp.de oder erweitern Sie Ihr Paket.
      <template v-if="hasClientWebsite">Auf Ihrer Website können Sie weiterhin unbegrenzt veröffentlichen.</template>
      <router-link :to="{name: 'packages.index', query: $route.query}" class="package-contingent__limit-link">
        Paketoptionen ansehen
      </router-link>
    </div>

  </section>
</template>

<script>
import {useUserStore} from "@/stores/user";
import ProgressBar from 'primevue/progressbar';
import FreePackageHint from "@/components/base_type/FreePackageHint.vue";
import {getBaseTypeThemeClass, getBaseTypeThemeStyle} from "@/utils/baseTypeTheme";
import {isFreePackage} from "@/utils/packagePolicy";
import {
  contingentQuantity,
  findBaseTypePackage,
  packageTypeLabel,
} from "@/utils/marketingContingent";

const PACKAGE_BASE_TYPE_BY_OBJECT_TYPE = {
  accommodations: 'unterkunft',
  offers: 'angebote',
  events: 'termine',
  discover: 'entdecken',
}

const TITLE_LABEL_BY_OBJECT_TYPE = {
  events: 'Ihr Kontingent für die Vermarktung der Veranstaltungen',
  offers: 'Ihr Kontingent für die Vermarktung der Angebote',
  discover: 'Ihr Kontingent für die Vermarktung im Bereich Entdecken',
  accommodations: 'Ihr Kontingent für die Vermarktung der Unterkünfte',
}

function websiteHost(url) {
  return String(url || '').trim().replace(/^[a-z]+:\/\//i, '').replace(/^www\./i, '').replace(/\/.*$/, '')
}

const PLURAL_LABEL_BY_OBJECT_TYPE = {
  events: 'Veranstaltungen',
  offers: 'Angebote',
  discover: 'Entdecken-Einträge',
  accommodations: 'Unterkünfte',
}

const SINGULAR_LABEL_BY_OBJECT_TYPE = {
  events: 'Veranstaltung',
  offers: 'Angebot',
  discover: 'Entdecken-Eintrag',
  accommodations: 'Unterkunft',
}

const CREATE_ROUTE_BY_OBJECT_TYPE = {
  accommodations: 'accommodations.new',
  offers: 'offers.new',
  events: 'events.new',
  discover: 'discover.new',
}

const CREATE_LABEL_BY_OBJECT_TYPE = {
  accommodations: 'Neue Unterkunft',
  offers: 'Neues Angebot',
  events: 'Neue Veranstaltung',
  discover: 'Neuer Entdecken-Eintrag',
}

export default {
  components: {
    FreePackageHint,
    ProgressBar,
  },
  props: {
    objectType: {
      default: () => 'offers',
      type: String,
    },
    // Number of entries currently shown on mvp.de; only these use the contingent.
    activeObjectCount: {
      default: () => 0,
      type: Number,
    },
    // URLs of the client's own websites; publishing there is not limited by the contingent.
    websiteUrls: {
      default: () => [],
      type: Array,
    },
    createRoute: {
      default: null,
      type: String,
    },
    createButtonLabel: {
      default: null,
      type: String,
    },
  },
  setup() {
    return {
      userStore: useUserStore(),
    }
  },
  computed: {
    titleLabel() {
      return TITLE_LABEL_BY_OBJECT_TYPE[this.objectType] || 'Ihr Kontingent für die Vermarktung'
    },
    websiteHosts() {
      const hosts = [...new Set(this.websiteUrls.map(websiteHost).filter(Boolean))]
      return hosts.length || !this.hasClientWebsite ? hosts : ['Ihrer Website']
    },
    hasClientWebsite() {
      return this.websiteUrls.length > 0
    },
    pluralLabel() {
      return PLURAL_LABEL_BY_OBJECT_TYPE[this.objectType] || 'Einträge'
    },
    capacityLabel() {
      const quantity = this.availableQuantity
      const label = quantity === 1 ? (SINGULAR_LABEL_BY_OBJECT_TYPE[this.objectType] || 'Eintrag') : this.pluralLabel
      return `Bis zu ${quantity} ${label} gleichzeitig auf mvp.de`
    },
    packageBaseType() {
      return PACKAGE_BASE_TYPE_BY_OBJECT_TYPE[this.objectType] || this.objectType
    },
    baseTypeThemeClass() {
      return getBaseTypeThemeClass(this.objectType)
    },
    baseTypeThemeStyle() {
      return getBaseTypeThemeStyle(this.objectType)
    },
    currentPackage() {
      return findBaseTypePackage(this.userStore.user.organisation?.active_packages, this.packageBaseType)
    },
    availableQuantity() {
      return contingentQuantity(this.currentPackage)
    },
    freeQuantity() {
      return Math.max(this.availableQuantity - this.activeObjectCount, 0)
    },
    isExhausted() {
      return this.activeObjectCount >= this.availableQuantity
    },
    calcCurrentProgress() {
      if (!this.availableQuantity) {
        return 0
      }

      return Math.min(this.activeObjectCount / this.availableQuantity, 1)
    },
    packageTypeLabel() {
      return packageTypeLabel(this.currentPackage)
    },
    isFreePackage() {
      return isFreePackage(this.currentPackage)
    },
    progressLabel() {
      const quantity = this.availableQuantity
      return `${this.activeObjectCount} von ${quantity} ${quantity === 1 ? 'Platz' : 'Plätzen'} belegt`
    },
    createRouteName() {
      return this.createRoute || CREATE_ROUTE_BY_OBJECT_TYPE[this.objectType] || 'accommodations.new'
    },
    createLabel() {
      return this.createButtonLabel || CREATE_LABEL_BY_OBJECT_TYPE[this.objectType] || 'Neu anlegen'
    },
  },
}
</script>

<style lang="scss">
.package-contingent {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border: 1px solid #d8e1ea;
  background:
    linear-gradient(180deg, #ffffff, #f8fbfd);
}

.package-contingent__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.package-contingent__header-right {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
}

.package-contingent__eyebrow {
  margin-bottom: 0.35rem;
  color: #5d7183;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.package-contingent__title {
  color: #10273d;
  font-size: 1.25rem;
}

.package-contingent__subtitle {
  color: #4e6477;
}

.package-contingent__usage {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 8rem;
  padding: 0.85rem 1rem;
  border: 1px solid #d8e1ea;
  background: #ffffff;
}

.package-contingent__usage strong {
  font-size: 1.45rem;
  line-height: 1;
  color: #10273d;
}

.package-contingent__usage span {
  margin-top: 0.25rem;
  color: #5d7183;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.package-contingent__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  color: #4e6477;
  font-size: 0.92rem;
}

.package-contingent__meta-item strong {
  color: #10273d;
  font-weight: 600;
}

.package-contingent__action-btn {
  border: 1px solid var(--base-type-primary);
  background-color: var(--base-type-primary) !important;
  color: #ffffff !important;
  font-weight: 800;
  box-shadow: 0 8px 18px rgba(16, 39, 61, 0.18);
}

.package-contingent__action-btn:hover,
.package-contingent__action-btn:focus-visible,
.package-contingent__action-btn:active {
  background-color: var(--base-type-secondary) !important;
  border-color: var(--base-type-secondary) !important;
  color: #ffffff !important;
}

.package-contingent__limit-note {
  padding: 0.85rem 1rem;
  border-left: 4px solid #dc3545;
  background: #fff1f2;
  color: #7a1c24;
}

.package-contingent__limit-link {
  margin-left: 0.35rem;
  color: inherit;
  font-weight: 700;
}

@media (max-width: 767px) {
  .package-contingent__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .package-contingent__usage {
    align-items: flex-start;
  }
}
</style>
